import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { AuthService } from '../../../core/services/auth.service';
import { CompanyService } from '../../../core/services/company.service';
import { CompanyActionsService } from '../../services/company-actions.service';

/**
 * Aviso de negocio inactivo (status 0) arriba de todas las páginas del negocio (BusinessShell y Diseñador).
 * - Sin sudo: el negocio queda en solo lectura hasta que un administrador de CradleRewards lo active.
 * - Sudo: «Activar negocio» con confirmación (toast con el message de la API).
 */
@Component({
  selector: 'app-inactive-banner',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (companies.inactive()) {
      <div class="banner" role="status">
        <span class="ic"><cr-icon name="alert" [size]="20" /></span>
        @if (auth.isSudo()) {
          <p class="txt"><b>Este negocio está inactivo.</b></p>
          <button type="button" class="cr-btn cr-btn--primary cr-btn--s" [disabled]="busy()" (click)="activate()">
            @if (busy()) { <span class="cr-spinner"></span> } @else { <cr-icon name="check" [size]="16" /> } Activar negocio
          </button>
        } @else {
          <p class="txt"><b>Este negocio está inactivo.</b> Un administrador de CradleRewards debe activarlo para que puedas usarlo y editarlo.</p>
        }
      </div>
    }
  `,
  styles: [`
    :host{display:block}
    .banner{display:flex;align-items:center;gap:12px;padding:12px 32px;background:var(--cr-warning-soft);color:var(--cr-text-primary);border-bottom:1px solid var(--cr-border-subtle)}
    .ic{color:var(--cr-warning);display:inline-flex;flex:none}
    .txt{flex:1;min-width:0;margin:0;font:400 14px/20px var(--cr-font-body)}
    .txt b{font-weight:600}
    .cr-btn{flex:none}
    @media (max-width:1199px){ .banner{padding:12px 24px} }
    @media (max-width:640px){ .banner{padding:12px 16px;flex-wrap:wrap;align-items:flex-start} .cr-btn{width:100%} }
  `],
})
export class InactiveBannerComponent {
  readonly auth = inject(AuthService);
  readonly companies = inject(CompanyService);
  private actions = inject(CompanyActionsService);
  readonly busy = signal(false);

  // region activate
  async activate(): Promise<void> {
    const c = this.companies.current();
    if (!c || this.busy()) return;
    this.busy.set(true);
    await this.actions.confirmStatus(c, 1);
    this.busy.set(false);
  }
}
